import styled from '@emotion/styled'
import { openBrowserPopup } from 'Helpers/browserPopup'
import {
  evalCondition,
  fromUTCtoZoneFormatted,
  notifyParent,
  STORE_KEY_ACCOUNT,
  STORE_KEY_LIST,
  storeGet
} from 'mailup-react-helpers'
import PropTypes from 'prop-types'
import { useEffect } from 'react'
import { useIntl } from 'react-intl'
import { useRecoilCallback, useRecoilValue } from 'recoil'

import LineNextToStep from 'Components/LineNextToStep/LineNextToStep'
import {
  Badge,
  BADGE_KIND_SECONDARY,
  BADGE_TYPE_DEFAULT,
  Flex,
  FlexItem,
  Grid,
  GridCell,
  Link,
  LINK_VARIANT_HIGHLIGHT,
  Panel,
  SKELETON_LOADER_VARIANT_ONWHITE,
  SkeletonLoader,
  SPACING_8,
  SpacingVertical,
  Text,
  TEXT_VARIANT_BOLD,
  TEXT_VARIANT_LIGHT,
  Title,
  TITLE_VARIANT_L,
  TruncateAndTooltip
} from 'mailup-web-components'
import StartStep from './StartStep'

import { initStepCallback } from 'Pages/WorkflowStatistics/recoil/callbacks/callbacks'
import {
  messageDetailsState,
  opacityConditionState
} from 'Pages/WorkflowStatistics/recoil/state/workflow'

import {
  WORKFLOW_MESSAGE_TYPE_EMAIL,
  WORKFLOW_MESSAGE_TYPE_SMS
} from 'Api/beAutomationApi'
import { searchState } from 'Pages/ContentsCampaignList/recoil/state/page'

const Step = ({ message, isLast, isFirst, index }) => {
  const intl = useIntl()
  const hasStats = evalCondition('uo:STAT')
  const { TimeZoneCode } = storeGet(STORE_KEY_LIST)

  const { IdMessage: idMessage, MessageType: messageType } = message

  const initStep = useRecoilCallback(initStepCallback, [])

  const {
    subject,
    timeConditionId,
    timeConditionDescription,
    hasTriggered,
    clicks,
    viewOrDelivered,
    send,
    loading,
    recipientExecutionDate
  } = useRecoilValue(messageDetailsState(idMessage))
  const opacityCondition = useRecoilValue(opacityConditionState(idMessage))
  const { totalItems } = useRecoilValue(searchState)

  useEffect(() => {
    idMessage && initStep(idMessage, messageType, isFirst)
  }, [idMessage, messageType])

  const onClickMsgName = () => {
    const { Domain } = storeGet(STORE_KEY_ACCOUNT)
    if (messageType === WORKFLOW_MESSAGE_TYPE_EMAIL) {
      const route = `https://${Domain}/console/messages_app_redirector.aspx?r=${encodeURIComponent(
        `/messages/email/${idMessage}/preview`
      )}`
      openBrowserPopup(route)
    } else {
      const route = `https://${Domain}/console/SMS_preview_window.aspx?idNL=${encodeURIComponent(
        `${idMessage}`
      )}`
      const options = { windowParams: { height: 400, width: 800 } }
      openBrowserPopup(route, {}, options)
    }
  }

  const goToStats = () => {
    notifyParent('redirectMainFrame', [
      `/console/statistics_app_redirector.aspx?r=/stats/sendouts/sending/mode/messages/0/${idMessage}`
    ])
  }

  const goToClickStats = () => {
    notifyParent('redirectMainFrame', [
      `/console/statistics_app_redirector.aspx?r=/stats/sendouts/link/mode/messages/0/${idMessage}`
    ])
  }

  const disabledCondition =
    !hasStats || messageType === WORKFLOW_MESSAGE_TYPE_SMS

  if (loading)
    return (
      <>
        {isFirst && <StartStep hasTriggered={hasTriggered} />}
        <Flex width='100%'>
          <FlexItem margin='0 32px 0 0' width='26px'>
            <LineNextToStep isDisabled isLast={isLast} />
          </FlexItem>
          <FlexItem width='100%'>
            <Panel>
              <Grid cols={12}>
                <GridCell span={7}>
                  <Text variant={TEXT_VARIANT_BOLD}>
                    {messageType === WORKFLOW_MESSAGE_TYPE_EMAIL &&
                      intl.formatMessage({
                        id: 'Load.AutomationWorkflow.DragElementMessage'
                      })}
                    {messageType === WORKFLOW_MESSAGE_TYPE_SMS &&
                      intl.formatMessage({
                        id: 'Load.AutomationWorkflow.DragElementMessageSms'
                      })}
                  </Text>
                  <SpacingVertical variant={SPACING_8} />
                  <SkeletonLoader
                    variant={SKELETON_LOADER_VARIANT_ONWHITE}
                    height='15px'
                    width='75%'
                  />
                  <SkeletonLoader
                    variant={SKELETON_LOADER_VARIANT_ONWHITE}
                    height='15px'
                    width='75%'
                  />
                </GridCell>
                <GridCell span={5}>
                  <Grid cols={12}>
                    <GridCell span={4}>
                      <SkeletonLoader
                        variant={SKELETON_LOADER_VARIANT_ONWHITE}
                        width='16px'
                        height='24px'
                      />
                      <Text variant={TEXT_VARIANT_LIGHT} htmlElement='p'>
                        {intl.formatMessage({
                          id: 'Load.MailupCommon.Sent'
                        })}
                      </Text>
                    </GridCell>
                    <GridCell span={4}>
                      <SkeletonLoader
                        variant={SKELETON_LOADER_VARIANT_ONWHITE}
                        width='16px'
                        height='24px'
                      />
                      <Text variant={TEXT_VARIANT_LIGHT} htmlElement='p'>
                        {messageType === WORKFLOW_MESSAGE_TYPE_EMAIL &&
                          intl.formatMessage({
                            id: 'Load.MailupCommon.Open'
                          })}
                        {messageType === WORKFLOW_MESSAGE_TYPE_SMS &&
                          intl.formatMessage({
                            id: 'Load.AutomationWorkflow.StatisticDelivered'
                          })}
                      </Text>
                    </GridCell>
                    <GridCell span={4}>
                      <SkeletonLoader
                        variant={SKELETON_LOADER_VARIANT_ONWHITE}
                        width='16px'
                        height='24px'
                      />
                      <Text variant={TEXT_VARIANT_LIGHT} htmlElement='p'>
                        {intl.formatMessage({ id: 'Load.Common.Clicks' })}
                      </Text>
                    </GridCell>
                  </Grid>
                </GridCell>
              </Grid>
            </Panel>
          </FlexItem>
        </Flex>
      </>
    )

  return (
    <>
      {isFirst && <StartStep hasTriggered={hasTriggered} />}
      <Flex width='100%' minWidth='800px'>
        <FlexItem margin='0 32px 0 0' width='26px'>
          <LineNextToStep isDisabled={!hasTriggered} isLast={isLast} />
        </FlexItem>
        <FlexItem width='100%'>
          <Panel
            data-mu-test={`workflow-statistics__panel__step-${
              isLast ? 'last' : index + 1
            }`}
          >
            <Grid cols={12}>
              <GridCell span={5}>
                <Text variant={TEXT_VARIANT_BOLD}>
                  {messageType === WORKFLOW_MESSAGE_TYPE_EMAIL &&
                    intl.formatMessage({
                      id: 'Load.AutomationWorkflow.DragElementMessage'
                    })}
                  {messageType === WORKFLOW_MESSAGE_TYPE_SMS &&
                    intl.formatMessage({
                      id: 'Load.AutomationWorkflow.DragElementMessageSms'
                    })}
                </Text>
                <SpacingVertical variant={SPACING_8} />
                <StyledContainer opacityCondition={opacityCondition}>
                  <Flex>
                    <Text variant={TEXT_VARIANT_LIGHT} htmlElement='span'>
                      {intl.formatMessage({
                        id: 'Load.MailupCommon.Message'
                      })}
                      {': '}
                    </Text>
                    <FlexItem margin='0 0 0 4px' width='100%'>
                      <Link
                        variant={LINK_VARIANT_HIGHLIGHT}
                        onClick={onClickMsgName}
                      >
                        <TruncateAndTooltip text={subject} />
                      </Link>
                    </FlexItem>
                  </Flex>
                  <Text variant={TEXT_VARIANT_LIGHT} htmlElement='p'>
                    {intl.formatMessage({
                      id: 'Load.MailupCommon.Id'
                    })}
                    {': '}
                    {idMessage}
                  </Text>
                  {timeConditionId > 0 && (
                    <Text variant={TEXT_VARIANT_LIGHT} htmlElement='p'>
                      {intl.formatMessage({
                        id: 'Load.AutomationWorkflowTroubleshooting.TimeCondition'
                      })}
                      {': '}
                      {timeConditionDescription}
                    </Text>
                  )}
                </StyledContainer>
              </GridCell>
              <GridCell span={2}>
                {totalItems >= 0 && recipientExecutionDate && (
                  <>
                    <Text variant={TEXT_VARIANT_BOLD}>
                      {intl.formatMessage({
                        id: 'Load.AutomationWorkflowTroubleshooting.ExecutionDate'
                      })}
                    </Text>
                    <Text>
                      {fromUTCtoZoneFormatted(
                        recipientExecutionDate,
                        TimeZoneCode,
                        'YYYY-MM-DD'
                      )}
                    </Text>
                  </>
                )}
              </GridCell>
              <GridCell span={5}>
                <StyledContainer opacityCondition={opacityCondition}>
                  <Grid cols={12}>
                    <GridCell span={4}>
                      <Title variant={TITLE_VARIANT_L}>
                        <Link
                          variant={LINK_VARIANT_HIGHLIGHT}
                          onClick={goToStats}
                          disabled={disabledCondition}
                        >
                          {intl.formatNumber(send)}
                        </Link>
                      </Title>
                      <Text variant={TEXT_VARIANT_LIGHT} htmlElement='p'>
                        {intl.formatMessage({
                          id: 'Load.MailupCommon.Sent'
                        })}
                      </Text>
                    </GridCell>
                    <GridCell span={4}>
                      <Title variant={TITLE_VARIANT_L}>
                        <Link
                          variant={LINK_VARIANT_HIGHLIGHT}
                          onClick={goToStats}
                          disabled={disabledCondition}
                        >
                          {intl.formatNumber(viewOrDelivered)}
                        </Link>
                      </Title>
                      <Text variant={TEXT_VARIANT_LIGHT} htmlElement='p'>
                        {messageType === WORKFLOW_MESSAGE_TYPE_EMAIL &&
                          intl.formatMessage({
                            id: 'Load.MailupCommon.Open'
                          })}
                        {messageType === WORKFLOW_MESSAGE_TYPE_SMS &&
                          intl.formatMessage({
                            id: 'Load.AutomationWorkflow.StatisticDelivered'
                          })}
                      </Text>
                    </GridCell>
                    <GridCell span={4}>
                      <Title variant={TITLE_VARIANT_L}>
                        <Link
                          variant={LINK_VARIANT_HIGHLIGHT}
                          onClick={goToClickStats}
                          disabled={disabledCondition}
                        >
                          {intl.formatNumber(clicks)}
                        </Link>
                      </Title>
                      <Text variant={TEXT_VARIANT_LIGHT} htmlElement='p'>
                        {intl.formatMessage({ id: 'Load.Common.Clicks' })}
                      </Text>
                    </GridCell>
                  </Grid>
                  {!hasTriggered && (
                    <>
                      <SpacingVertical variant={SPACING_8} />

                      <Badge
                        kind={BADGE_KIND_SECONDARY}
                        type={BADGE_TYPE_DEFAULT}
                        text={intl.formatMessage({
                          id: 'Load.AutomationWorkflowTroubleshooting.ConditionNeverHappened'
                        })}
                      />
                    </>
                  )}
                </StyledContainer>
              </GridCell>
            </Grid>
          </Panel>
        </FlexItem>
      </Flex>
    </>
  )
}

export default Step

Step.propTypes = {
  message: PropTypes.object,
  isLast: PropTypes.bool,
  isFirst: PropTypes.bool,
  index: PropTypes.number
}

const StyledContainer = styled.span`
  opacity: ${({ opacityCondition }) =>
    opacityCondition ? '0.5' : '1'} !important;
`
